import { create } from "zustand";

import {
  endOfWeek,
  isCurrentWeek,
  isToday,
  nextDay,
  nextWeekEndDay,
  prevDay,
  prevWeekEndDay,
  startOfWeek,
} from "@/config/date-fns";

type DailySlice = {
  date: Date;
  isToday: boolean;
  next: VoidFunction;
  prev: VoidFunction;
  select: (date: Date) => void;
};

type DailySliceWithoutFunction = Omit<DailySlice, "next" | "prev" | "select">;

function parseDailySlice(date: Date): DailySliceWithoutFunction {
  return {
    date,
    isToday: isToday(date),
  };
}

type WeeklySlice = {
  isCurrentWeek: boolean;
  next: VoidFunction;
  prev: VoidFunction;
  select: (date: Date) => void;
  weekEnd: Date;
  weekStart: Date;
};

type WeeklySliceWithoutFunction = Omit<WeeklySlice, "next" | "prev" | "select">;

function parseWeeklySlice(date: Date): WeeklySliceWithoutFunction {
  return {
    isCurrentWeek: isCurrentWeek(date),
    weekEnd: endOfWeek(date),
    weekStart: startOfWeek(date),
  };
}

// グローバルなストア型
type CalendarStore = {
  daily: DailySlice;
  weekly: WeeklySlice;
};

export const useCalendarStore = create<CalendarStore>((set) => {
  const initDate = new Date();

  return {
    daily: {
      ...parseDailySlice(initDate),
      next: () =>
        set((state) => {
          if (state.daily.isToday) return state;

          const nextDate = nextDay(state.daily.date);
          return { ...state, daily: { ...state.daily, ...parseDailySlice(nextDate) } };
        }),
      prev: () =>
        set((state) => {
          const prevDate = prevDay(state.daily.date);
          return { ...state, daily: { ...state.daily, ...parseDailySlice(prevDate) } };
        }),
      select: (date) =>
        set((state) => ({
          ...state,
          daily: { ...state.daily, ...parseDailySlice(date) },
        })),
    },
    weekly: {
      ...parseWeeklySlice(initDate),
      next: () =>
        set((state) => {
          if (state.weekly.isCurrentWeek) return state;

          const nextWeekEnd = nextWeekEndDay(state.weekly.weekEnd);

          return {
            ...state,
            weekly: {
              ...state.weekly,
              ...parseWeeklySlice(nextWeekEnd),
            },
          };
        }),
      prev: () =>
        set((state) => {
          const prevWeekEnd = prevWeekEndDay(state.weekly.weekEnd);

          return {
            ...state,
            weekly: {
              ...state.weekly,
              ...parseWeeklySlice(prevWeekEnd),
            },
          };
        }),
      select: (date) =>
        set((state) => {
          return {
            ...state,
            weekly: {
              ...state.weekly,
              ...parseWeeklySlice(date),
            },
          };
        }),
    },
  };
});
